/**
 * Rulers along the top and left, and an optional grid over the page.
 *
 * Both are DOM, not canvas. They belong to the view rather than the artwork, so
 * keeping them out of the Konva tree means they cannot be selected, cannot be
 * hit-tested, and cannot end up in a thumbnail read off the stage.
 *
 * Ticks are in whichever unit the customer chose - millimetres, centimetres,
 * inches or points - and the labelled interval changes with zoom: a ruler that
 * prints a number every millimetre at 20% is a grey smear, and one that prints
 * every 50 mm at 800% is not a ruler. The corner where the two meet is the
 * button that changes the unit, which is where a designer looks for it.
 */

import { useEffect, useRef, useState } from 'react';
import { useEditor } from '../state/store';
import { UNITS, mmToPx, pxToMm, unitInfo, type Unit } from '../model/units';
import { snapGuide, type Axis } from './snapping';

/** How thick the rulers are, in CSS pixels. Matches --tb-ruler. */
export const RULER = 20;

/**
 * The spacings a ruler may label, in each unit, smallest first.
 *
 * Series that read naturally in that unit: nobody wants an inch ruler marked
 * every 0.3 in, or a point ruler every 15 pt.
 */
const LABELS: Record<Unit, number[]> = {
  mm: [1, 2, 5, 10, 20, 25, 50, 100, 200, 500, 1000],
  cm: [0.1, 0.2, 0.5, 1, 2, 5, 10, 20, 50, 100],
  in: [0.125, 0.25, 0.5, 1, 2, 5, 10, 20, 50],
  pt: [5, 10, 20, 50, 100, 200, 500, 1000, 2000],
};

/** How a labelled interval divides into unlabelled ticks, finest first. */
const DIVISIONS: Record<Unit, number[]> = {
  mm: [10, 5, 2],
  cm: [10, 5, 2],
  // Halves all the way down, which is how an inch ruler is printed.
  in: [16, 8, 4, 2],
  pt: [10, 5, 2],
};

/** Room each label needs, in screen pixels. */
const LABEL_PX = 56;

/** Closest two unlabelled ticks may sit before they become a smear. */
const MINOR_PX = 5;

/**
 * The labelled spacing at this zoom, and the ticks between, in millimetres.
 *
 * @return step and minor, both in mm; minor is 0 when there is no room for any.
 */
export function interval(zoom: number, unit: Unit): { step: number; minor: number; label: number } {
  const perMm = unitInfo(unit).perMm;
  const series = LABELS[unit];
  const label = series.find((each) => mmToPx(each / perMm, zoom) >= LABEL_PX) ?? series[series.length - 1]!;
  const step = label / perMm;

  const parts = DIVISIONS[unit].find((count) => mmToPx(step / count, zoom) >= MINOR_PX);

  return { step, minor: parts ? step / parts : 0, label };
}

export function Rulers() {
  const view = useEditor((s) => s.view);
  const page = useEditor((s) => s.doc?.pages[s.pageIndex] ?? null);

  if (!view.showRulers || view.preview || !page) return null;

  return (
    <>
      <Ruler axis="x" page={page.size.w} />
      <Ruler axis="y" page={page.size.h} />

      {/* The corner, so the two rulers meet rather than overlap - and the unit. */}
      <UnitCorner />
    </>
  );
}

/**
 * Pulling a guide off a ruler.
 *
 * The top ruler gives a horizontal guide and the left one a vertical guide,
 * which is the way round every design tool does it. The line follows the
 * pointer and snaps to the trim, the centre, the safe line and the folds on the
 * way down; letting go over the rulers themselves places nothing, so a mis-drag
 * costs nothing.
 */
function dragGuideOff(from: 'x' | 'y', event: React.PointerEvent<HTMLDivElement>) {
  const stage = document.querySelector('.tb-canvas__stage');
  const store = useEditor.getState();
  const doc = store.doc;
  const page = doc?.pages[store.pageIndex] ?? null;

  if (!stage || !doc || !page) return;

  const box = stage.getBoundingClientRect();
  const axis: Axis = from === 'x' ? 'y' : 'x';

  const where = (at: { clientX: number; clientY: number }) => {
    const view = useEditor.getState().view;

    return axis === 'y'
      ? pxToMm(at.clientY - box.top - view.y, view.zoom)
      : pxToMm(at.clientX - box.left - view.x, view.zoom);
  };

  const settle = (at: { clientX: number; clientY: number }) =>
    snapGuide(axis, where(at), page, doc.safe, useEditor.getState().view.zoom);

  const move = (moved: PointerEvent) => {
    const snapped = settle(moved);
    const now = useEditor.getState();

    now.setDrafting({ axis, at: snapped.at });
    now.setGuides(snapped.guides);
  };

  const up = (ended: PointerEvent) => {
    window.removeEventListener('pointermove', move);
    window.removeEventListener('pointerup', up);

    const now = useEditor.getState();

    now.setDrafting(null);
    now.setGuides([]);

    // Let go over the rulers and nothing is placed.
    if (ended.clientX < box.left || ended.clientY < box.top) return;

    // Placing one while they are hidden would drop it into nowhere.
    if (!now.view.showGuides) now.setView({ showGuides: true });

    now.addGuide(axis, settle(ended).at);
  };

  window.addEventListener('pointermove', move);
  window.addEventListener('pointerup', up);
  move(event.nativeEvent);
}

function Ruler({ axis, page }: { axis: 'x' | 'y'; page: number }) {
  const view = useEditor((s) => s.view);
  const unit = useEditor((s) => s.unit);
  const info = unitInfo(unit);

  const { step, minor, label } = interval(view.zoom, unit);
  const origin = axis === 'x' ? view.x : view.y;
  const stepPx = mmToPx(step, view.zoom);

  // Two extra intervals either side, so ticks reach the edges rather than
  // stopping where the page does. Counted in whole intervals, so a label is
  // always a whole number of them from the page's corner.
  const first = -Math.ceil(origin / stepPx) - 1;
  const marks: number[] = [];

  for (let n = first; n < first + 400; n++) {
    const at = origin + n * stepPx;

    if (at > 4000) break;

    marks.push(n);
  }

  /*
   * The small ticks are a repeating gradient rather than a few hundred spans:
   * one element, positioned so a tick lands on the page's edge, and the browser
   * draws the rest.
   */
  const minorPx = minor ? mmToPx(minor, view.zoom) : 0;
  const minorStyle = minorPx
    ? axis === 'x'
      ? {
          backgroundSize: `${minorPx}px 5px`,
          backgroundPosition: `${origin}px 100%`,
        }
      : {
          backgroundSize: `5px ${minorPx}px`,
          backgroundPosition: `100% ${origin}px`,
        }
    : { backgroundImage: 'none' };

  return (
    <div
      className={`tb-ruler tb-ruler--${axis}`}
      aria-hidden="true"
      data-unit={unit}
      onPointerDown={(event) => {
        event.preventDefault();
        dragGuideOff(axis, event);
      }}
    >
      <div className="tb-ruler__minor" style={minorStyle} />

      {marks.map((n) => {
        const at = origin + n * stepPx;
        const mm = n * step;

        return (
          <span
            key={n}
            className={`tb-ruler__tick${mm >= -0.001 && mm <= page + 0.001 ? ' tb-ruler__tick--on' : ''}`}
            style={axis === 'x' ? { left: at } : { top: at }}
          >
            {readable(n * label, info.decimals)}
          </span>
        );
      })}
    </div>
  );
}

/**
 * A ruler number: whole where it can be, and never more decimals than the
 * series needs - 0.25 in, 1.5 cm, 100 pt.
 */
function readable(value: number, decimals: number): string {
  const rounded = Math.round(value * Math.pow(10, decimals)) / Math.pow(10, decimals);

  return String(rounded);
}

/**
 * Where the rulers meet: the unit they are in, and a menu to change it.
 *
 * The same choice changes every length in the editor - positions, sizes,
 * corners, outline widths - not only the rulers. Type stays in points.
 */
function UnitCorner() {
  const unit = useEditor((s) => s.unit);
  const setUnit = useEditor((s) => s.setUnit);
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const away = (event: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(event.target as Node)) setOpen(false);
    };
    const key = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;

      event.preventDefault();
      setOpen(false);
    };

    window.addEventListener('mousedown', away);
    window.addEventListener('keydown', key, true);

    return () => {
      window.removeEventListener('mousedown', away);
      window.removeEventListener('keydown', key, true);
    };
  }, [open]);

  return (
    <div className="tb-ruler__corner" ref={wrap}>
      <button
        type="button"
        className="tb-ruler__unit"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Units: ${unitInfo(unit).name}. Change`}
        title="Change units"
        onClick={() => setOpen(!open)}
      >
        {unitInfo(unit).short}
      </button>

      {open && (
        <div className="tb-menu tb-ruler__menu" role="menu" aria-label="Units">
          {UNITS.map((each) => (
            <button
              key={each.id}
              type="button"
              role="menuitemradio"
              aria-checked={each.id === unit}
              className="tb-menu__item"
              onClick={() => {
                setUnit(each.id);
                setOpen(false);
              }}
            >
              <span className="tb-menu__tick" aria-hidden="true">{each.id === unit ? '✓' : ''}</span>
              <span className="tb-menu__label">{each.name}</span>
              <kbd className="tb-menu__keys">{each.short}</kbd>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * The grid spacing in each unit, in millimetres: 5 mm, half a centimetre, a
 * quarter inch, a pica. Round numbers in whatever the customer is reading.
 */
export function gridStep(unit: Unit): number {
  switch (unit) {
    case 'in':
      return 25.4 / 4;
    case 'pt':
      return (12 * 25.4) / 72;
    default:
      return 5;
  }
}

/** The grid spacing as the bottom bar says it. */
export function gridLabel(unit: Unit): string {
  switch (unit) {
    case 'in':
      return '¼ in';
    case 'pt':
      return '12 pt';
    case 'cm':
      return '0.5 cm';
    default:
      return '5 mm';
  }
}

/**
 * The grid, drawn behind the artwork.
 *
 * A CSS gradient rather than a few hundred Konva lines: it costs one element,
 * scrolls with the page for free, and never shows up in a hit test.
 */
export function Grid() {
  const view = useEditor((s) => s.view);
  const unit = useEditor((s) => s.unit);
  const page = useEditor((s) => s.doc?.pages[s.pageIndex] ?? null);

  if (!view.showGrid || view.preview || !page) return null;

  const step = mmToPx(gridStep(unit), view.zoom);

  // Below this the lines merge into a solid tone and stop meaning anything.
  if (step < 4) return null;

  return (
    <div
      className="tb-grid"
      aria-hidden="true"
      style={{
        left: view.x,
        top: view.y,
        width: mmToPx(page.size.w, view.zoom),
        height: mmToPx(page.size.h, view.zoom),
        backgroundSize: `${step}px ${step}px`,
      }}
    />
  );
}
